import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import AppButton from '../../../../components/designe/app-button/AppButton';
import { useAccess } from '../../../../hooks/useAccess';
import { useGetLanguages } from '../../../../services/redux/api/languagesAPI';
import {
  useCreateNotificationCampaignMutation,
  useDispatchBulkNotificationsMutation,
  useGetNotificationTemplatesQuery,
  usePreviewNotificationMutation,
} from '../../../../services/redux/api/notificationsAdminApi';
import {
  NOTIFICATION_BULK_MAX_USER_IDS,
  NOTIFICATION_DISPLAY_DOMAINS,
  type NotificationDisplayDomain,
  type NotificationPreferenceGroup,
  type NotificationTemplateCategory,
} from '../../../../types/interfaces/notification-admin';
import {
  isCategoryPreferenceConsistent,
  preferenceGroupsForCategory,
} from '../../../../utils/notification-admin-rules';
import {
  notificationCategoryLabel,
  notificationDisplayDomainLabel,
  notificationPreferenceGroupLabel,
} from '../../../../utils/notification-template-labels';
import theme from '../../../../styles/theme';
import { useNotificationsTableStyles } from '../NotificationsTable.styles';

function isScheduleInFuture(localValue: string): boolean {
  if (!localValue.trim()) return false;
  const t = new Date(localValue).getTime();
  return !Number.isNaN(t) && t > Date.now() + 55_000;
}

function parseUserIds(raw: string): string[] {
  const parts = raw.split(/[\s,;]+/);
  const out: string[] = [];
  for (const p of parts) {
    const s = p.trim();
    if (s) out.push(s);
  }
  return out;
}

function bulkDispatchErrorMessage(err: unknown): string {
  if (typeof err === 'object' && err !== null && 'data' in err) {
    const d = (err as { data?: { message?: string } }).data?.message;
    if (typeof d === 'string' && d.trim()) return d;
  }
  if (typeof err === 'object' && err !== null && 'message' in err) {
    const m = (err as { message?: string }).message;
    if (typeof m === 'string' && m.trim()) return m;
  }
  if (err instanceof Error && err.message) return err.message;
  return '';
}

function parseOptionalJsonRecord(
  raw: string,
): Record<string, unknown> | null | 'invalid' {
  const t = raw.trim();
  if (t === '' || t === '{}') return null;
  try {
    const v = JSON.parse(t);
    if (v && typeof v === 'object' && !Array.isArray(v))
      return v as Record<string, unknown>;
  } catch {
    return 'invalid';
  }
  return 'invalid';
}

export function NotificationDispatchPage() {
  const { t } = useTranslation();
  const { classes } = useNotificationsTableStyles(theme);
  const { access } = useAccess();
  const canMutate =
    (access?.notifications?.canCreate || access?.notifications?.canEdit) ??
    false;

  const { data: templates = [] } = useGetNotificationTemplatesQuery();
  const { data: languages = [] } = useGetLanguages();
  const [createCampaign, { isLoading: creating }] =
    useCreateNotificationCampaignMutation();
  const [dispatchBulk, { isLoading: bulkLoading }] =
    useDispatchBulkNotificationsMutation();
  const [preview, { isLoading: previewLoading }] =
    usePreviewNotificationMutation();

  const systemTemplates = useMemo(
    () =>
      templates.filter(
        (x) =>
          x.category === 'transactional' &&
          x.preferenceGroup === 'system' &&
          x.active,
      ),
    [templates],
  );

  const activeTemplates = useMemo(
    () => templates.filter((x) => x.active),
    [templates],
  );

  const [sysTemplate, setSysTemplate] = useState('');
  const [sysLang, setSysLang] = useState('');
  const [sysScheduled, setSysScheduled] = useState('');
  const [sysVarsJson, setSysVarsJson] = useState('{}');

  const [bulkKind, setBulkKind] = useState<'template' | 'inline'>('template');
  const [bulkUserIdsRaw, setBulkUserIdsRaw] = useState('');
  const [bulkTemplate, setBulkTemplate] = useState('');
  const [bulkVarsJson, setBulkVarsJson] = useState('{}');
  const [bulkBatchKey, setBulkBatchKey] = useState('');
  const [bulkTitle, setBulkTitle] = useState('');
  const [bulkBody, setBulkBody] = useState('');
  const [bulkCategory, setBulkCategory] =
    useState<NotificationTemplateCategory>('marketing');
  const [bulkPreference, setBulkPreference] =
    useState<NotificationPreferenceGroup>('marketing');
  const [bulkDomain, setBulkDomain] =
    useState<NotificationDisplayDomain>('marketing');
  const [bulkDataJson, setBulkDataJson] = useState('{}');

  const [bulkError, setBulkError] = useState<string | null>(null);
  const [bulkSuccess, setBulkSuccess] = useState<{
    batchKey: string;
    queuedCount: number;
  } | null>(null);

  const [singleTemplate, setSingleTemplate] = useState('');
  const [singleUserId, setSingleUserId] = useState('');
  const [singleLang, setSingleLang] = useState('');
  const [singleVars, setSingleVars] = useState('{}');
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewBody, setPreviewBody] = useState('');

  useEffect(() => {
    if (sysTemplate || systemTemplates.length === 0) return;
    setSysTemplate(systemTemplates[0].code);
  }, [systemTemplates, sysTemplate]);

  useEffect(() => {
    if (singleTemplate || systemTemplates.length === 0) return;
    setSingleTemplate(systemTemplates[0].code);
  }, [systemTemplates, singleTemplate]);

  useEffect(() => {
    if (bulkTemplate || activeTemplates.length === 0) return;
    setBulkTemplate(activeTemplates[0].code);
  }, [activeTemplates, bulkTemplate]);

  const scheduledIso =
    sysScheduled.trim() === ''
      ? null
      : (() => {
          const d = new Date(sysScheduled);
          return Number.isNaN(d.getTime()) ? null : d.toISOString();
        })();

  const scheduleOk = isScheduleInFuture(sysScheduled);
  const sysVarsParsed = parseOptionalJsonRecord(sysVarsJson);
  const sysVarsInvalid = sysVarsParsed === 'invalid';

  const canSubmitSystem =
    canMutate &&
    !!sysTemplate &&
    !!sysLang &&
    scheduleOk &&
    !!scheduledIso &&
    !sysVarsInvalid;

  const submitSystem = async () => {
    if (!canSubmitSystem || !scheduledIso) return;
    const parsed = parseOptionalJsonRecord(sysVarsJson);
    const tv = parsed === 'invalid' || parsed === null ? null : parsed;
    await createCampaign({
      templateCode: sysTemplate,
      templateVariablesJson: tv,
      filterJson: { languageId: sysLang },
      scheduledAt: scheduledIso,
      status: 'scheduled',
    }).unwrap();
    setSysScheduled('');
  };

  const onBulkCategoryChange = (c: NotificationTemplateCategory) => {
    setBulkCategory(c);
    const allowed = preferenceGroupsForCategory(c);
    if (c === 'marketing') {
      setBulkPreference('marketing');
    } else if (!allowed.includes(bulkPreference)) {
      setBulkPreference(allowed[0]);
    }
  };

  const resolvedBulkPreference: NotificationPreferenceGroup =
    bulkCategory === 'marketing' ? 'marketing' : bulkPreference;

  const submitBulk = async () => {
    setBulkError(null);
    setBulkSuccess(null);
    const userIds = parseUserIds(bulkUserIdsRaw);
    if (userIds.length === 0) {
      setBulkError(t('adminDashboard.notifications.dispatch.bulkNoUserIds'));
      return;
    }
    if (userIds.length > NOTIFICATION_BULK_MAX_USER_IDS) {
      setBulkError(
        t('adminDashboard.notifications.dispatch.bulkTooManyUserIds', {
          max: NOTIFICATION_BULK_MAX_USER_IDS,
        }),
      );
      return;
    }

    try {
      if (bulkKind === 'template') {
        if (!bulkTemplate.trim()) return;
        const tv = parseOptionalJsonRecord(bulkVarsJson);
        if (tv === 'invalid') {
          setBulkError(
            t(
              'adminDashboard.notifications.campaigns.templateVariablesInvalid',
            ),
          );
          return;
        }
        const res = await dispatchBulk({
          kind: 'template',
          userIds,
          templateCode: bulkTemplate.trim(),
          templateVariables: tv ?? undefined,
          batchKey: bulkBatchKey.trim() || undefined,
        }).unwrap();
        setBulkSuccess(res);
        return;
      }

      if (!bulkTitle.trim() || !bulkBody.trim()) {
        setBulkError(
          t('adminDashboard.notifications.campaigns.inlineCopyRequired'),
        );
        return;
      }
      if (
        !isCategoryPreferenceConsistent(bulkCategory, resolvedBulkPreference)
      ) {
        setBulkError(
          t('adminDashboard.notifications.campaigns.inlineMetaInvalid'),
        );
        return;
      }
      const data = parseOptionalJsonRecord(bulkDataJson);
      if (data === 'invalid') {
        setBulkError(
          t('adminDashboard.notifications.dispatch.bulkDataInvalid'),
        );
        return;
      }
      const res = await dispatchBulk({
        kind: 'inline',
        userIds,
        title: bulkTitle.trim(),
        body: bulkBody.trim(),
        category: bulkCategory,
        preferenceGroup: resolvedBulkPreference,
        displayDomain: bulkDomain,
        data: data ?? undefined,
        batchKey: bulkBatchKey.trim() || undefined,
      }).unwrap();
      setBulkSuccess(res);
    } catch (err: unknown) {
      const msg = bulkDispatchErrorMessage(err);
      setBulkError(
        msg || t('adminDashboard.notifications.dispatch.bulkRequestFailed'),
      );
    }
  };

  const runSinglePreview = async () => {
    if (!singleTemplate.trim()) return;
    let variables: Record<string, unknown> | undefined;
    try {
      const parsed = JSON.parse(singleVars || '{}');
      variables =
        parsed && typeof parsed === 'object' && !Array.isArray(parsed)
          ? (parsed as Record<string, unknown>)
          : {};
    } catch {
      return;
    }
    const res = await preview({
      templateCode: singleTemplate,
      languageId: singleLang || undefined,
      userId: singleUserId.trim() || undefined,
      variables,
    }).unwrap();
    setPreviewTitle(res.title);
    setPreviewBody(res.body);
  };

  return (
    <div className={classes.wrapper}>
      <Typography variant="h6" gutterBottom>
        {t('adminDashboard.notifications.dispatch.pageTitle')}
      </Typography>

      <Typography variant="body2" color="text.secondary" paragraph>
        {t('adminDashboard.notifications.dispatch.pageIntro')}
      </Typography>

      <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
        {t('adminDashboard.notifications.dispatch.systemSectionTitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        {t('adminDashboard.notifications.dispatch.systemSectionBody')}
      </Typography>

      <FormControl fullWidth sx={{ mb: 2 }} disabled={!canMutate}>
        <InputLabel id="ds-tpl">
          {t('adminDashboard.notifications.dispatch.systemTemplate')}
        </InputLabel>
        <Select
          labelId="ds-tpl"
          label={t('adminDashboard.notifications.dispatch.systemTemplate')}
          value={sysTemplate}
          onChange={(e) => setSysTemplate(e.target.value as string)}>
          {systemTemplates.length === 0 ? (
            <MenuItem value="" disabled>
              {t('adminDashboard.notifications.dispatch.noSystemTemplates')}
            </MenuItem>
          ) : (
            systemTemplates.map((tpl) => (
              <MenuItem key={tpl.id} value={tpl.code}>
                {tpl.code}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>

      <FormControl fullWidth sx={{ mb: 2 }} disabled={!canMutate}>
        <InputLabel id="ds-lang">
          {t('adminDashboard.notifications.campaigns.targetLanguage')}
        </InputLabel>
        <Select
          labelId="ds-lang"
          label={t('adminDashboard.notifications.campaigns.targetLanguage')}
          value={sysLang}
          onChange={(e) => setSysLang(e.target.value as string)}>
          {languages
            .filter((l) => l.id)
            .map((l) => (
              <MenuItem key={l.id} value={l.id}>
                {l.title || l.key}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      <TextField
        label={t(
          'adminDashboard.notifications.campaigns.templateVariablesJson',
        )}
        value={sysVarsJson}
        onChange={(e) => setSysVarsJson(e.target.value)}
        fullWidth
        multiline
        minRows={3}
        disabled={!canMutate}
        sx={{ mb: 2 }}
        error={sysVarsInvalid}
        helperText={
          sysVarsInvalid
            ? t(
                'adminDashboard.notifications.campaigns.templateVariablesInvalid',
              )
            : t('adminDashboard.notifications.dispatch.systemVarsHint')
        }
      />

      <TextField
        label={t('adminDashboard.notifications.campaigns.scheduledAtUtc')}
        value={sysScheduled}
        onChange={(e) => setSysScheduled(e.target.value)}
        fullWidth
        type="datetime-local"
        disabled={!canMutate}
        InputLabelProps={{ shrink: true }}
        sx={{ mb: 1 }}
        helperText={t(
          'adminDashboard.notifications.dispatch.systemScheduleHint',
        )}
      />
      {sysScheduled.trim() !== '' && !scheduleOk && (
        <Typography
          variant="caption"
          color="error"
          display="block"
          sx={{ mb: 2 }}>
          {t('adminDashboard.notifications.dispatch.scheduleMustBeFuture')}
        </Typography>
      )}

      {canMutate && (
        <AppButton
          onClick={submitSystem}
          loader={creating}
          disabled={!canSubmitSystem}>
          {t('adminDashboard.notifications.dispatch.createSystemCampaign')}
        </AppButton>
      )}

      <Divider sx={{ my: 4 }} />

      <Typography variant="subtitle1" gutterBottom>
        {t('adminDashboard.notifications.dispatch.bulkSectionTitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        {t('adminDashboard.notifications.dispatch.bulkSectionBody')}
      </Typography>

      <TextField
        label={t('adminDashboard.notifications.dispatch.bulkUserIds')}
        value={bulkUserIdsRaw}
        onChange={(e) => setBulkUserIdsRaw(e.target.value)}
        fullWidth
        multiline
        minRows={3}
        disabled={!canMutate}
        sx={{ mb: 2 }}
        helperText={t('adminDashboard.notifications.dispatch.bulkUserIdsHint')}
      />

      <FormControl component="fieldset" sx={{ mb: 2 }} disabled={!canMutate}>
        <Typography variant="subtitle2" gutterBottom>
          {t('adminDashboard.notifications.dispatch.bulkKind')}
        </Typography>
        <RadioGroup
          row
          value={bulkKind}
          onChange={(e) =>
            setBulkKind(e.target.value as 'template' | 'inline')
          }>
          <FormControlLabel
            value="template"
            control={<Radio />}
            label={t('adminDashboard.notifications.campaigns.kindTemplate')}
          />
          <FormControlLabel
            value="inline"
            control={<Radio />}
            label={t('adminDashboard.notifications.campaigns.kindInline')}
          />
        </RadioGroup>
      </FormControl>

      {bulkKind === 'template' && (
        <>
          <FormControl fullWidth sx={{ mb: 2 }} disabled={!canMutate}>
            <InputLabel id="bk-tpl">
              {t('adminDashboard.notifications.campaigns.templateCode')}
            </InputLabel>
            <Select
              labelId="bk-tpl"
              label={t('adminDashboard.notifications.campaigns.templateCode')}
              value={bulkTemplate}
              onChange={(e) => setBulkTemplate(e.target.value as string)}>
              {activeTemplates.length === 0 ? (
                <MenuItem value="" disabled>
                  {t('adminDashboard.notifications.campaigns.noTemplates')}
                </MenuItem>
              ) : (
                activeTemplates.map((tpl) => (
                  <MenuItem key={tpl.id} value={tpl.code}>
                    {tpl.code} ({notificationCategoryLabel(t, tpl.category)})
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          <TextField
            label={t(
              'adminDashboard.notifications.campaigns.templateVariablesJson',
            )}
            value={bulkVarsJson}
            onChange={(e) => setBulkVarsJson(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            disabled={!canMutate}
            sx={{ mb: 2 }}
          />
        </>
      )}

      {bulkKind === 'inline' && (
        <>
          <TextField
            label={t('common.title')}
            value={bulkTitle}
            onChange={(e) => setBulkTitle(e.target.value)}
            fullWidth
            disabled={!canMutate}
            sx={{ mb: 2 }}
          />
          <TextField
            label={t('common.description')}
            value={bulkBody}
            onChange={(e) => setBulkBody(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            disabled={!canMutate}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }} disabled={!canMutate}>
            <InputLabel id="bk-cat">
              {t('adminDashboard.notifications.templates.category')}
            </InputLabel>
            <Select
              labelId="bk-cat"
              label={t('adminDashboard.notifications.templates.category')}
              value={bulkCategory}
              onChange={(e) =>
                onBulkCategoryChange(
                  e.target.value as NotificationTemplateCategory,
                )
              }>
              {(['marketing', 'transactional'] as const).map((c) => (
                <MenuItem key={c} value={c}>
                  {notificationCategoryLabel(t, c)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }} disabled={!canMutate}>
            <InputLabel id="bk-pref">
              {t('adminDashboard.notifications.templates.preferenceGroup')}
            </InputLabel>
            <Select
              labelId="bk-pref"
              label={t(
                'adminDashboard.notifications.templates.preferenceGroup',
              )}
              value={resolvedBulkPreference}
              onChange={(e) =>
                setBulkPreference(e.target.value as NotificationPreferenceGroup)
              }>
              {preferenceGroupsForCategory(bulkCategory).map((p) => (
                <MenuItem key={p} value={p}>
                  {notificationPreferenceGroupLabel(t, p)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }} disabled={!canMutate}>
            <InputLabel id="bk-dom">
              {t('adminDashboard.notifications.templates.displayDomain')}
            </InputLabel>
            <Select
              labelId="bk-dom"
              label={t('adminDashboard.notifications.templates.displayDomain')}
              value={bulkDomain}
              onChange={(e) =>
                setBulkDomain(e.target.value as NotificationDisplayDomain)
              }>
              {NOTIFICATION_DISPLAY_DOMAINS.map((d) => (
                <MenuItem key={d} value={d}>
                  {notificationDisplayDomainLabel(t, d)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={t('adminDashboard.notifications.dispatch.bulkDataJson')}
            value={bulkDataJson}
            onChange={(e) => setBulkDataJson(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            disabled={!canMutate}
            sx={{ mb: 2 }}
            helperText={t('adminDashboard.notifications.dispatch.bulkDataHint')}
          />
        </>
      )}

      <TextField
        label={t('adminDashboard.notifications.dispatch.batchKey')}
        value={bulkBatchKey}
        onChange={(e) => setBulkBatchKey(e.target.value)}
        fullWidth
        disabled={!canMutate}
        sx={{ mb: 2 }}
        helperText={t('adminDashboard.notifications.dispatch.batchKeyHint')}
      />

      {bulkError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {bulkError}
        </Alert>
      )}
      {bulkSuccess && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setBulkSuccess(null)}>
          {t('adminDashboard.notifications.dispatch.bulkQueued', {
            count: bulkSuccess.queuedCount,
            key: bulkSuccess.batchKey,
          })}
        </Alert>
      )}

      {canMutate && (
        <AppButton
          onClick={submitBulk}
          loader={bulkLoading}
          sx={{ mb: 2 }}
          disabled={
            !parseUserIds(bulkUserIdsRaw).length ||
            (bulkKind === 'template' && !bulkTemplate.trim()) ||
            (bulkKind === 'inline' && (!bulkTitle.trim() || !bulkBody.trim()))
          }>
          {t('adminDashboard.notifications.dispatch.bulkSubmit')}
        </AppButton>
      )}

      <Divider sx={{ my: 4 }} />

      <Typography variant="subtitle1" gutterBottom>
        {t('adminDashboard.notifications.dispatch.previewSectionTitle')}
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        {t('adminDashboard.notifications.dispatch.previewSectionInfo')}
      </Alert>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="sg-tpl">
          {t('adminDashboard.notifications.dispatch.systemTemplate')}
        </InputLabel>
        <Select
          labelId="sg-tpl"
          label={t('adminDashboard.notifications.dispatch.systemTemplate')}
          value={singleTemplate}
          onChange={(e) => setSingleTemplate(e.target.value as string)}>
          {systemTemplates.length === 0 ? (
            <MenuItem value="" disabled>
              {t('adminDashboard.notifications.dispatch.noSystemTemplates')}
            </MenuItem>
          ) : (
            systemTemplates.map((tpl) => (
              <MenuItem key={tpl.id} value={tpl.code}>
                {tpl.code}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>

      <TextField
        label={t('adminDashboard.notifications.preview.userId')}
        value={singleUserId}
        onChange={(e) => setSingleUserId(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="sg-lang">{t('common.language')}</InputLabel>
        <Select
          labelId="sg-lang"
          label={t('common.language')}
          value={singleLang}
          onChange={(e) => setSingleLang(e.target.value as string)}>
          <MenuItem value="">
            <em>{t('adminDashboard.notifications.preview.autoLanguage')}</em>
          </MenuItem>
          {languages
            .filter((l) => l.id)
            .map((l) => (
              <MenuItem key={l.id} value={l.id}>
                {l.title || l.key}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      <TextField
        label={t('adminDashboard.notifications.preview.variablesJson')}
        value={singleVars}
        onChange={(e) => setSingleVars(e.target.value)}
        fullWidth
        multiline
        minRows={3}
        sx={{ mb: 2 }}
      />

      <AppButton
        onClick={runSinglePreview}
        loader={previewLoading}
        sx={{ mb: 2 }}>
        {t('adminDashboard.notifications.preview.run')}
      </AppButton>

      {(previewTitle || previewBody) && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="subtitle2">{t('common.title')}</Typography>
          <Typography variant="body2" paragraph>
            {previewTitle}
          </Typography>
          <Typography variant="subtitle2">{t('common.description')}</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {previewBody}
          </Typography>
        </Box>
      )}
    </div>
  );
}
